import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormIPSubnetField } from '@ipfabric/ui-components/FormIPSubnetField/FormIPSubnetField';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import {
  type IPSubnetInputValueOrDraft,
  getIPSubnetZodValidationError,
  isIpSubnetInputValidValue,
} from '@ipfabric/ui-components/IPSubnetInput/IPSubnetInput.types';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import { useMemo } from 'react';
import { generateGuid } from 'utils/guid';
import { z } from 'zod';
import type {
  AimApplicationDraft,
  AimWorkloadDraft,
} from '../../../model/aimDrafts';
import { AimEntityCombobox } from './AimEntityCombobox';

export const aimWorkloadFormSchema = z.object({
  applicationId: z
    .string({ required_error: 'Application is required' })
    .nullable()
    .refine((v): v is string => v !== null && v !== '', {
      message: 'Application is required',
    })
    .pipe(z.string().uuid('Application is required')),
  name: z.string().min(1, 'Name is required'),
  ipAddress: z
    .string()
    .transform((v) => (v === '' ? null : v))
    .pipe(z.string().ip('Invalid IP address').nullable()),
  interfaces: z
    .custom<IPSubnetInputValueOrDraft>()
    .refine(isIpSubnetInputValidValue, getIPSubnetZodValidationError)
    .refine(
      (v) => !isIpSubnetInputValidValue(v) || v.ips.length > 0,
      'At least one interface IP is required',
    ),
});

type AimWorkloadFormInputValues = {
  applicationId: string | null;
  name: string;
  ipAddress: string;
  interfaces: IPSubnetInputValueOrDraft;
};

type AimWorkloadFormOutputValues = z.output<typeof aimWorkloadFormSchema>;

export const toAimWorkloadFormValues = (
  row?: AimWorkloadDraft,
): AimWorkloadFormInputValues => ({
  applicationId: row?.applicationId ?? null,
  name: row?.name ?? '',
  ipAddress: row?.ipAddress ?? '',
  interfaces: { ips: row?.interfaces ?? [] },
});

type Props = {
  formType: 'add' | 'edit';
  initialValues?: AimWorkloadDraft;
  availableApplications: AimApplicationDraft[];
  onClose: () => void;
  onSubmit: (entity: AimWorkloadDraft) => void;
};

export const AimWorkloadDialog = ({
  formType,
  initialValues,
  availableApplications,
  onClose,
  onSubmit,
}: Props) => {
  const form = useCustomForm<
    AimWorkloadFormInputValues,
    unknown,
    AimWorkloadFormOutputValues
  >({
    resolver: zodResolver(aimWorkloadFormSchema),
    defaultValues: toAimWorkloadFormValues(initialValues),
  });

  const applicationOptions = useMemo(
    () => availableApplications.map((a) => ({ value: a.id, label: a.name })),
    [availableApplications],
  );

  const handleSave = form.handleSubmit((values) => {
    const interfaces: string[] = isIpSubnetInputValidValue(values.interfaces)
      ? values.interfaces.ips
      : [];
    const entity: AimWorkloadDraft = {
      id:
        formType === 'edit' && initialValues
          ? initialValues.id
          : generateGuid(),
      applicationId: values.applicationId,
      name: values.name,
      ipAddress: values.ipAddress,
      interfaces,
    };
    onSubmit(entity);
    onClose();
  });

  return (
    <SidePanel
      title={
        formType === 'add' ? 'Add manual workload' : 'Edit manual workload'
      }
      closeOnBackdropClick={false}
      onClose={onClose}
      footer={({ closeModal }) => (
        <div className='flex gap-xs'>
          <Button
            className='flex-1'
            variant='primary'
            type='submit'
            icon='fa-floppy-disk'
            disabled={!form.formState.isDirty}
            loading={form.formState.isSubmitting}
            onClick={handleSave}
            dataTestId='aim-workload-save'
          >
            {formType === 'add' ? 'Add manual workload' : 'Save changes'}
          </Button>
          <Button className='flex-1' variant='outline' onClick={closeModal}>
            Close
          </Button>
        </div>
      )}
    >
      <form>
        <Card className='flex flex-col gap p-sm'>
          <AimEntityCombobox
            control={form.control}
            name='applicationId'
            label='Application'
            options={applicationOptions}
            emptyOptionsHint='Add an Application first to create Workloads.'
          />
          <FormTextField
            type='text'
            control={form.control}
            name='name'
            label='Name'
          />
          <FormTextField
            type='text'
            control={form.control}
            name='ipAddress'
            label='Public IP Address (optional)'
          />
          <FormIPSubnetField
            control={form.control}
            name='interfaces'
            label='Interfaces'
            description='List of IP adresses on workload interfaces.'
            type='ip'
          />
        </Card>
      </form>
    </SidePanel>
  );
};
